
import React, { useState } from 'react';
import { signUp } from '../services/cognito';
import { crmService } from '../services/api';
import { getAppMarket } from '../services/MarketService';
import SocialLoginButtons from './Shared/SocialLoginButtons';
import { trackSignup } from '../services/analytics';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import styles from './SignUpForm.module.css';

import { PhoneNumberUtil } from 'google-libphonenumber';

const SignUpForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+44');
  const [units, setUnits] = useState('');
  const [complianceCondition, setComplianceCondition] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState(false);
  const [error, setError] = useState('');
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan');

  const phoneUtil = PhoneNumberUtil.getInstance();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!complianceCondition) {
      setError('You must agree to the Terms and Conditions.');
      return;
    }
    try {
      // 0. Format Phone Number — UX-10: strip spaces, parentheses, dashes and
      // dots BEFORE validation so "(832) 555-0198" / "020 7946 0958" signup works.
      const normalizedPhone = phoneNumber.replace(/[\s().-]+/g, '');
      const fullPhone = normalizedPhone.startsWith('+') ? normalizedPhone : `${countryCode}${normalizedPhone.replace(/^0+/, '')}`;

      // 0b. Validate Phone Number — only when one was supplied.
      // Phone is optional (2026-11-03): the UK prod pool requires only sub +
      // email, so an empty phone field must never block sign-up.
      if (normalizedPhone) {
        try {
          const number = phoneUtil.parseAndKeepRawInput(fullPhone);
          if (!phoneUtil.isValidNumber(number)) {
            setError('Please enter a valid phone number for the selected country.');
            return;
          }
        } catch (error) {
          setError('Please enter a valid phone number format.');
          return;
        }
      }

      // 1. Create Cognito User
      await signUp(email, password, givenName, familyName, fullPhone, units);

      // 2. Defer Sync to Brevo until after Login (requires Auth)
      const contactData = {
        email,
        firstName: givenName,
        lastName: familyName,
        phone: fullPhone,
        units: parseInt(units) || 0,
        marketingConsent: privacyPolicy,
        plan: plan || undefined
      };
      sessionStorage.setItem('pending_brevo_sync', JSON.stringify(contactData));

      // User is auto-confirmed by preSignUp trigger — show success popup
      // temp_auth stored so LoginForm can auto-login without re-typing credentials
      sessionStorage.setItem('temp_auth', JSON.stringify({ email, password }));
      trackSignup('email');
      setSignUpSuccess(true);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const isFSP = window.location.hostname.includes('food-safety') || window.location.hostname.includes('fsp');

  return (

    <div>
      <div className={styles.container}>
        <div className={styles.formWrapper}>
          <div className={styles.logoContainer}>
            <Link className={styles.logoContainerLink} to="/">
              {isFSP ? (
                <img src="/FSP-Logo.png" alt="FSP Manager" className={styles.logo} />
              ) : (
                <img src="/Landlord-Pro-Logo.png" alt="Landlord Pro" className={styles.logo} />
              )}
            </Link>
          </div>
          <div className={styles.card}>
            <div className={styles.formSection}>
              <form onSubmit={handleSubmit}>
                <label className={styles.label}>E-mail</label>
                <input className={styles.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required />

                <label className={styles.label}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className={styles.input}
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#666'
                    }}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
                <p className={styles.hint} style={{ fontSize: '12px', color: '#666', marginTop: '4px', marginBottom: '12px' }}>
                  Password must be at least 8 characters with uppercase, lowercase, number, and special character.
                </p>

                <label className={styles.label}>First Name</label>
                <input className={styles.input} type="text" value={givenName} onChange={(e) => setGivenName(e.target.value)} placeholder="First Name" required />

                <label className={styles.label}>Last Name</label>
                <input className={styles.input} type="text" value={familyName} onChange={(e) => setFamilyName(e.target.value)} placeholder="Last Name" required />

                <label className={styles.label}>Phone Number <span style={{ fontWeight: 400, color: '#666' }}>(optional)</span></label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <select
                    className={styles.input}
                    style={{ width: '80px' }}
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                  >
                    <option value="+44">UK (+44)</option>
                    <option value="+1">US/Canada (+1)</option>
                    <option value="+852">Hong Kong (+852)</option>
                    <option value="+86">China (+86)</option>
                    <option value="+65">Singapore (+65)</option>
                    <option value="+31">Netherlands (+31)</option>
                  </select>
                  <input
                    className={styles.input}
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Phone Number (optional)"
                  />
                </div>
                <label className={styles.label}>How many Property UNITS</label>
                <input className={styles.input} type="text" value={units} onChange={(e) => setUnits(e.target.value)} placeholder="UNITS" required />

                <label className={styles.checkboxLabel} htmlFor="compliance_checkbox">
                  <input
                    id="compliance_checkbox"
                    type="checkbox"
                    name="terms_and_conditions"
                    checked={complianceCondition}
                    onChange={(e) => {
                      setComplianceCondition(e.target.checked);
                      if (e.target.checked) setError('');
                    }}
                  />
                  {' '}I agree to compliance engine's <a href="https://compliance-engine.io/terms-of-service/" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
                </label>
                <br />
                <label className={styles.checkboxLabel} htmlFor="privacy_checkbox">
                  <input
                    id="privacy_checkbox"
                    type="checkbox"
                    name="terms_and_conditions2"
                    checked={privacyPolicy}
                    onChange={(e) => setPrivacyPolicy(e.target.checked)}
                  />
                  {' '}I want to receive marketing emails from Compliance - Engine.
                </label><br />
                {error && <p className={styles.error}>{error}</p>}
                <button
                  type="submit"
                  className={styles.signupButton}
                  style={isFSP ? { backgroundColor: '#ea580c' } : {}}
                >
                  <span>Sign Up</span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>

                {/* E-58 social sign-up (Google / Microsoft / Apple) — Texas market only */}
                <SocialLoginButtons context="signup" />

                <button type="button" onClick={() => navigate('/login')} className={styles.backToLoginButton}>
                  Back to Login
                </button>
              </form>
            </div>
            <div className={styles.bottomSection}>
              <div className={styles.bottomGrid}>
                <div className={styles.leftAlign}>
                  <button className={styles.utilityButton}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                    </svg>
                    <span>Forgot Password</span>
                  </button>
                </div>
                <div className={styles.rightAlign}>
                  <button className={styles.utilityButton}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    <span>Help</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Confirmation Popup */}
      {signUpSuccess && (
        <div className={styles.overlay}>
          <div className={styles.successModal}>
            <div className={styles.successIcon}>✓</div>
            <h2 className={styles.successTitle}>Registration Successful!</h2>
            <p className={styles.successMessage}>
              Your account has been created and you're ready to go.
            </p>
            <div className={styles.successSteps}>
              <h3 className={styles.stepsTitle}>What to expect next:</h3>
              <ul className={styles.stepsList}>
                <li>You'll be auto-logged in to your account</li>
                <li>Check your email for your welcome details</li>
                <li>Add your first property to get started</li>
                <li>{getAppMarket() === 'texas' ? 'Complete rental registration if required' : 'Complete PRS registration if required'}</li>
              </ul>
            </div>
            <button
              onClick={() => navigate('/login?verified=1')}
              className={styles.continueButton}
            >
              Continue to Dashboard
            </button>
          </div>
        </div>
      )}

    </div>

  );
};

export default SignUpForm;